import type { ActivePointer, InputActionId, InputActionResolver, InputPoint } from "./types";

export type InputTransitionType = "press" | "move" | "release" | "cancel" | "release-all";

export interface InputTransitionEvent {
  type: InputTransitionType;
  pointerId: number;
  action: InputActionId | null;
  point?: InputPoint;
}

export type InputTransitionObserver = (transition: InputTransitionEvent) => void;

type MappedPointer = ActivePointer & { action: InputActionId };

export class InputService {
  private readonly pointers = new Map<number, MappedPointer>();
  private readonly unmappedPointers = new Map<number, InputPoint>();
  private readonly actionOwners = new Map<InputActionId, Set<number>>();

  constructor(
    private resolveAction: InputActionResolver,
    private readonly observeTransition: InputTransitionObserver = () => undefined,
  ) {}

  setResolver(resolveAction: InputActionResolver): void {
    this.releaseAll();
    this.resolveAction = resolveAction;
  }

  press(pointerId: number, point: InputPoint): void {
    this.release(pointerId);
    const action = this.resolveAction(point);
    if (action === null) {
      this.unmappedPointers.set(pointerId, { ...point });
      this.observeTransition({ type: "press", pointerId, action: null, point: { ...point } });
      return;
    }

    const active: MappedPointer = {
      pointerId,
      action,
      point: { ...point },
    };
    this.pointers.set(pointerId, active);

    let owners = this.actionOwners.get(action);
    if (!owners) {
      owners = new Set<number>();
      this.actionOwners.set(action, owners);
    }
    owners.add(pointerId);
    this.observeTransition({ type: "press", pointerId, action, point: { ...point } });
  }

  move(pointerId: number, point: InputPoint): void {
    const active = this.pointers.get(pointerId);
    if (active) {
      active.point = { ...point };
      this.observeTransition({
        type: "move",
        pointerId,
        action: active.action,
        point: { ...point },
      });
      return;
    }

    if (!this.unmappedPointers.has(pointerId)) return;
    this.unmappedPointers.set(pointerId, { ...point });
    this.observeTransition({ type: "move", pointerId, action: null, point: { ...point } });
  }

  release(pointerId: number): void {
    this.clearPointer(pointerId, "release");
  }

  cancel(pointerId: number): void {
    this.clearPointer(pointerId, "cancel");
  }

  releaseAll(): void {
    const activePointers = [...this.pointers.values()];
    const unmappedPointers = [...this.unmappedPointers.entries()];
    this.pointers.clear();
    this.unmappedPointers.clear();
    this.actionOwners.clear();

    for (const active of activePointers) {
      this.observeTransition({
        type: "release-all",
        pointerId: active.pointerId,
        action: active.action,
        point: { ...active.point },
      });
    }
    for (const [pointerId, point] of unmappedPointers) {
      this.observeTransition({
        type: "release-all",
        pointerId,
        action: null,
        point: { ...point },
      });
    }
  }

  isActive(action: InputActionId): boolean {
    return (this.actionOwners.get(action)?.size ?? 0) > 0;
  }

  activePointerIds(): number[] {
    return [...this.pointers.keys(), ...this.unmappedPointers.keys()].sort((a, b) => a - b);
  }

  snapshot(): ActivePointer[] {
    const mapped = [...this.pointers.values()].map((active) => ({
      pointerId: active.pointerId,
      action: active.action,
      point: { ...active.point },
    }));
    const unmapped = [...this.unmappedPointers.entries()].map(([pointerId, point]) => ({
      pointerId,
      action: null,
      point: { ...point },
    }));
    return [...mapped, ...unmapped].sort((a, b) => a.pointerId - b.pointerId);
  }

  destroy(): void {
    this.releaseAll();
  }

  private clearPointer(pointerId: number, type: "release" | "cancel"): void {
    const active = this.pointers.get(pointerId);
    if (active) {
      this.pointers.delete(pointerId);
      const owners = this.actionOwners.get(active.action);
      owners?.delete(pointerId);
      if (owners?.size === 0) this.actionOwners.delete(active.action);

      this.observeTransition({
        type,
        pointerId,
        action: active.action,
        point: { ...active.point },
      });
      return;
    }

    const point = this.unmappedPointers.get(pointerId);
    if (!point) return;
    this.unmappedPointers.delete(pointerId);
    this.observeTransition({ type, pointerId, action: null, point: { ...point } });
  }
}
